/**
 * Optimized queries for Sanity
 * These queries are designed to minimize API CDN requests and bandwidth usage
 */

// Artwork queries - with projection to fetch only needed fields
export const allArtworksQuery = `*[_type == "artwork"] | order(_createdAt desc)[0...20] {
  _id,
  title,
  "slug": slug.current,
  "imageUrl": image.asset->url,
  description,
  "year": coalesce(year, 'Unknown'),
  "medium": coalesce(medium, 'Mixed Media'),
  price,
  "artist": coalesce(artist->name, 'Unknown Artist'),
  "artistImageUrl": artist->profileImage.asset->url,
  "edition": {
    "total": coalesce(edition.total, 1),
    "available": coalesce(edition.available, 1)
  }
}`;

// Programme (events) queries - Present (ongoing events)
export const presentEventsQuery = `*[_type == "event" && dateTime(startDate) <= dateTime(now()) && (endDate == null || dateTime(endDate) >= dateTime(now()))] | order(startDate asc)[0...20] {
  _id,
  title,
  eventType,
  description,
  startDate,
  endDate,
  openingReception,
  closingReception,
  galleryHours,
  venue,
  venueAddress,
  "slug": slug.current,
  "bannerImageUrl": bannerImage.asset->url,
  "organizers": organizers[]->{ 
    name, 
    role,
    "profileImageUrl": profileImage.asset->url
  },
  "featuredArtworks": featuredArtworks[0..2]->{ title, "imageUrl": image.asset->url }
}`;

// Programme (events) queries - Future (upcoming events)
export const futureEventsQuery = `*[_type == "event" && dateTime(startDate) > dateTime(now())] | order(startDate asc)[0...20] {
  _id,
  title,
  eventType,
  description,
  startDate,
  endDate,
  openingReception,
  closingReception,
  galleryHours,
  venue,
  venueAddress,
  "slug": slug.current,
  "bannerImageUrl": bannerImage.asset->url,
  "organizers": organizers[]->{ 
    name, 
    role,
    "profileImageUrl": profileImage.asset->url
  },
  "featuredArtworks": featuredArtworks[0..2]->{ title, "imageUrl": image.asset->url }
}`;

// Programme (events) queries - Archive (past events)
export const archivedEventsQuery = `*[_type == "event" && (endDate != null && dateTime(endDate) < dateTime(now()) || (endDate == null && dateTime(startDate) < dateTime(now())))] | order(coalesce(endDate, startDate) desc)[0...50] {
  _id,
  title,
  eventType,
  "slug": slug.current,
  startDate,
  endDate,
  openingReception,
  closingReception,
  galleryHours,
  venue,
  venueAddress,
  "bannerImageUrl": bannerImage.asset->url
}`;

// For backward compatibility
export const allEventsQuery = presentEventsQuery;

// Blog post queries - paginated and with content projection
export const allBlogPostsQuery = `*[_type == "blogPost"] | order(publishedDate desc)[0...10] {
  _id,
  title,
  slug,
  postType,
  publishedDate,
  "author": author->name,
  "authorImageUrl": author->profileImage.asset->url,
  // Only fetch a preview of content for list views
  "contentPreview": array::join(string::split(pt::text(content), " ")[0..50], " ") + "...",
  "featuredImageUrl": featuredImage.asset->url,
  "tags": tags[]->name
}`;

// Note: singleBlogPostQuery is defined below

// Single item queries
export const singleArtworkQuery = (slug: string) => `
  *[_type == "artwork" && slug.current == "${slug}"][0] {
    _id,
    title,
    slug,
    "imageUrl": image.asset->url,
    description,
    price,
    "artist": artist->name,
    "edition": edition->{
      total,
      available
    }
  }
`;

export const singleEventQuery = (id: string) => `
  *[_type == "event" && _id == "${id}"][0] {
    _id,
    title,
    eventType,
    description,
    startDate,
    endDate,
    openingReception,
    closingReception,
    galleryHours,
    location,
    "bannerImageUrl": bannerImage.asset->url,
    "organizers": organizers[]->{ name, role },
    "featuredArtworks": featuredArtworks[]->{ title, "imageUrl": image.asset->url }
  }
`;

// Single blog post query - optimized to fetch full content only when viewing a specific post
export const singleBlogPostQuery = (slug: string) => `
  *[_type == "blogPost" && slug.current == "${slug}"][0] {
    _id,
    title,
    "slug": slug.current,
    postType,
    publishedDate,
    "author": author->name,
    "authorImageUrl": author->profileImage.asset->url,
    content,  // Full content for single post view
    "featuredImageUrl": featuredImage.asset->url,
    "tags": tags[]->name
  }
`;
